import React, { useState, useEffect } from "react";
import callApi from "../../constants/API/CallApi";
import { ShowToast, Severty } from "../../helper/toast";
import { Modal, Form, Col, Input, InputNumber, Select, Row, Button, Space, Checkbox } from "antd";
import { PlusOutlined, MinusCircleOutlined } from "@ant-design/icons";
import { updateBelt, getBelt, ListClass, getBranches } from "../../constants/API/API";
import useApi from "../../hooks/useApi";

const { Option } = Select;
const BeltUpdate = ({ editVisible, setEditVisible, selectedBelt, refresh }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [classes, setClasses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [beltData, setBeltData] = useState(null);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [orientation, setOrientation] = useState(null); // null, 'isHorizontal', or 'isVertical'

  useEffect(() => {
    if (selectedBelt && editVisible) {
      getBelt(selectedBelt._id)
        .then((response) => {
          if (response.status === 200 && response.data) {
            setBeltData(response.data);
          }
        })
        .catch((err) => {
          ShowToast(err.message || "Failed to fetch belt data", Severty.ERROR);
        });
    }
  }, [selectedBelt, editVisible]);

  useEffect(() => {
    if (beltData) {
      const branchId = beltData.branchId?._id || beltData.branchId;
      
      // Set orientation based on beltData
      if (beltData.isHorizontal) {
        setOrientation('isHorizontal');
      } else if (beltData.isVertical) {
        setOrientation('isVertical');
      } else {
        setOrientation(null);
      }
      
      form.setFieldsValue({
        name: beltData.name,
        colorCode: beltData.colorCode && beltData.colorCode.length > 0 ? beltData.colorCode : ["#000000"],
        stripeColor: beltData.stripeColor,
        stripeCount: beltData.stripeCount,
        reviewLink: beltData.reviewLink,
        GrowthPromptType: beltData.GrowthPromptType,
        message: beltData.message,
        beltLevel: beltData.beltLevel,
        classId: beltData.classId?._id,
        branchId: branchId,
      });
    }
  }, [beltData, form]);

  useApi({
    callback: ListClass,
    dependencies: [refresh],
    shouldCallApi: true,
    onSuccess: ({ list }) => {
      setClasses(list);
    },
  });

  useEffect(() => {
    if (editVisible) {
      setBranchesLoading(true);
      getBranches()
        .then(({ data }) => {
          const branchList = data?.data || [];
          const activeBranches = branchList.filter(
            (branch) => branch.isActive
          );
          setBranches(activeBranches);
          setBranchesLoading(false);
        })
        .catch((err) => {
          ShowToast(err.message || "Failed to fetch branches", Severty.ERROR);
          setBranchesLoading(false);
        });
    }
  }, [editVisible]);

  const handleOk = async (values) => {
    setLoading(true);
    
    // Create payload with orientation key
    const payload = {
      ...values,
    };
    
    if (orientation === 'isHorizontal') {
      payload.isHorizontal = true;
      payload.isVertical = false;
    } else if (orientation === 'isVertical') {
      payload.isHorizontal = false;
      payload.isVertical = true;
    } else {
      payload.isHorizontal = false;
      payload.isVertical = false;
    }
    
    callApi({
      callback: updateBelt,
      payload: [payload, selectedBelt._id],
      onSuccess: ({ message }) => {
        ShowToast(message, Severty.SUCCESS);
        form.resetFields();
        setOrientation(null);
        refresh(new Date().getTime());
        setEditVisible(false);
        setLoading(false);
      },
      onError: (err) => {
        ShowToast(err, Severty.ERROR);
        setLoading(false);
      },
    });
  };

  const handleCancel = () => {
    setEditVisible(false);
    form.resetFields();
    setBeltData(null);
    setBranches([]);
    setBranchesLoading(false);
    setOrientation(null);
  };

  const validateBeltName = (rule, value) => {
    if (!value) {
      return Promise.reject("Please enter a belt name");
    } else if (!/^[A-Z][a-zA-Z0-9 ]*$/.test(value)) {
      return Promise.reject(
        "Belt name must start with a capital letter and contain only letters, numbers and spaces"
      );
    }
    return Promise.resolve();
  };

  return (
    <Modal
      open={editVisible}
      className="tab_modal"
      title="Update Belt"
      okText="Update"
      onCancel={handleCancel}
      okButtonProps={{
        form: "update-belt",
        htmlType: "submit",
        loading: loading,
      }}
      width={800}
      destroyOnClose
    >
      <Form
        id="update-belt"
        form={form}
        onFinish={handleOk}
        layout="vertical"
        preserve={false}
      >
        <Row gutter={16}>

          <Col xs={24} md={12}>
            <Form.Item
              name="name"
              label="Belt Name"
              rules={[{ required: true, validator: validateBeltName }]}
            >
              <Input placeholder="Enter belt name" />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="stripeCount"
              label="Stripe Count"
              rules={[
                { required: true, message: "Stripe Count is require" },
                {
                  type: "number",
                  min: 0,
                  max: 10,
                  message: "Stripe Count must be between 0 and 10"
                }
              ]}
            >
              <InputNumber style={{ width: "100%" }} placeholder="Enter Stripe Count" />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="branchId"
              label="Branch"
              rules={[{ required: true, message: "Please select a branch" }]}
            >
              <Select
                placeholder="Select branch"
                showSearch
                disabled
                loading={branchesLoading}
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {Array.isArray(branches) &&
                  branches.map((branch) => (
                    <Option key={branch._id} value={branch._id}>
                      {branch.name}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="GrowthPromptType"
              label="Growth Prompt Type"
              rules={[{ required: true, message: "Please select growth prompt type" }]}
            >
              <Select placeholder="Select Prompt Type">
                <Option value="None">None</Option>
                <Option value="Leave a Review">Leave a Review</Option>
                <Option value="Invite a Friend">Invite a Friend</Option>
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.List name="colorCode">
              {(fields, { add, remove }) => (
                <>
                  <div style={{ marginBottom: 8 }}>Color Code</div>
                  {fields.map((field) => (
                    <Space key={field.key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                      <Form.Item
                        {...field}
                        rules={[{ required: true, message: "Please enter a color code" }]}
                        style={{ margin: 0 }}
                      >
                        <Input
                          type="color"
                          style={{
                            width: 60,
                            padding: 0,
                            border: "none",
                            background: "none",
                          }}
                        />
                      </Form.Item>
                      {fields.length > 1 && (
                        <MinusCircleOutlined onClick={() => remove(field.name)} style={{ color: 'red', cursor: 'pointer' }} />
                      )}
                    </Space>
                  ))}
                  {fields.length < 3 && (
                    <Form.Item style={{ margin: 0 }}>
                      <Button type="dashed" onClick={() => add("#000000")} block icon={<PlusOutlined />}>
                        Add Color
                      </Button>
                    </Form.Item>
                  )}
                </>
              )}
            </Form.List>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="stripeColor"
              label="Stripe Color Code"
              rules={[{ required: true, message: "Please enter a stripe color code" }]}
            >
              <Input
                type="color"
                style={{
                  width: 60,
                  padding: 0,
                  border: "none",
                  background: "none",
                }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="reviewLink"
              label="Review Link"
              rules={[
                { required: true, message: "Please enter review link" }
              ]}
            >
              <Input placeholder="Enter Review Link" />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              name="message"
              label="Message"
              rules={[
                { required: true, message: "Message is required" },
                { min: 20, message: "Message must be at least 20 characters" }
              ]}
            >
              <Input.TextArea
                rows={3}
                placeholder="Enter message"
                maxLength={200}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item label="Orientation">
              <Space direction="vertical">
                <Checkbox
                  checked={orientation === 'isHorizontal'}
                  onChange={(e) => {
                    setOrientation(e.target.checked ? 'isHorizontal' : null);
                  }}
                >
                  Horizontal
                </Checkbox>
                <Checkbox
                  checked={orientation === 'isVertical'}
                  onChange={(e) => {
                    setOrientation(e.target.checked ? 'isVertical' : null);
                  }}
                >
                  Vertical
                </Checkbox>
              </Space>
            </Form.Item>
          </Col>

        </Row>
      </Form>
    </Modal>
  );
};

export default BeltUpdate;
